import React, { useCallback, useMemo, useState } from 'react';
import { Text } from '@heartinternet/hi-ui';
import { Link } from '@heartinternet/hi-ui';
import { Button } from '@heartinternet/hi-ui';
import { Flex } from '@heartinternet/hi-ui';
import { Notice } from '@heartinternet/hi-ui';
import { Table } from '@heartinternet/hi-ui';
import type { Column } from '@heartinternet/hi-ui';
import { useEditDropdown } from './useEditDropdown';
import type { HostingPackageType, HostingPackageTypesPageProps } from './types';
import './HostingPackageTypesPage.css';

export type { HostingPackageType, HostingPackageTypesPageProps };

const WIZARD_URL = '/manage/manage-package-types-wizard.cgi';
const CREATE_ACCOUNT_URL = '/manage/manage-reseller-add.cgi';

export const HostingPackageTypesPage: React.FC<HostingPackageTypesPageProps> = ({
  resoldTypes,
  done,
}) => {
  const [sortKey, setSortKey] = useState<string>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const { renderTrigger, renderPortal } = useEditDropdown();

  const sortedTypes = useMemo(() => {
    return [...resoldTypes].sort((a, b) => {
      let cmp: number;
      if (sortKey === 'id') {
        cmp = a.id - b.id;
      } else if (sortKey === 'web_server_type') {
        cmp = a.web_server_type.localeCompare(b.web_server_type);
      } else {
        cmp = a.name.localeCompare(b.name);
      }
      return sortDirection === 'asc' ? cmp : -cmp;
    });
  }, [resoldTypes, sortKey, sortDirection]);

  const handleSort = useCallback((key: string, direction: 'asc' | 'desc') => {
    setSortKey(key);
    setSortDirection(direction);
  }, []);

  const columns = useMemo<Column<HostingPackageType>[]>(
    () => [
      {
        key: 'name',
        label: 'Web Hosting Package',
        sortable: true,
        render: (_value, row) => (
          <Text variant="body-small" inline>
            {row.name}
          </Text>
        ),
      },
      {
        key: 'web_server_type',
        label: 'Type',
        sortable: true,
        render: (_value, row) => (
          <Text variant="body-small" inline>
            {row.web_server_type === 'windows' ? 'Windows' : 'Linux'}
          </Text>
        ),
      },
      {
        key: 'id',
        label: 'ID',
        sortable: true,
        render: (_value, row) => (
          <Text variant="body-small" inline>
            {row.id}
          </Text>
        ),
      },
      {
        key: 'manage',
        label: '',
        align: 'right' as const,
        render: (_value, row) => renderTrigger(row),
      },
    ],
    [renderTrigger]
  );

  return (
    <div className="hi-ui-hosting-package-types-page">
      <div className="hi-ui-hosting-package-types-page__header">
        <Text variant="heading-1">Configure Hosting Packages</Text>

        {done === 'add' && (
          <Notice variant="success">
            Package completion successful. Click <Link href={CREATE_ACCOUNT_URL}>here</Link> to
            create a shared hosting account based on this package.
          </Notice>
        )}
        {done === 'edit' && (
          <Notice variant="success">
            You&apos;ve just updated a web hosting package type! Click{' '}
            <Link href={CREATE_ACCOUNT_URL}>here</Link> to create a shared hosting account based on
            this package.
          </Notice>
        )}
        {done === 'delete' && <Notice variant="success">Package deletion successful.</Notice>}

        <Text variant="body">Create and edit your hosting packages from this page.</Text>
        <Text variant="body">
          Each hosting account must be assigned a package, which defines its features (such as
          storage, bandwidth, and mailboxes). We&rsquo;ve included two example packages to get you
          started, but we recommend creating your own to suit your needs.
        </Text>
      </div>

      <Flex align="center" justify="start" className="hi-ui-hosting-package-types-page__toolbar">
        <Button as="a" href={WIZARD_URL}>
          Create new package
        </Button>
      </Flex>

      {resoldTypes.length > 0 && (
        <Table
          columns={columns}
          data={sortedTypes}
          gridColumns="2fr 1fr 1fr 80px"
          sortKey={sortKey}
          sortDirection={sortDirection}
          onSort={handleSort}
        />
      )}
      {renderPortal()}
    </div>
  );
};
